import { useAuth } from "@/hooks/use-auth";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import {
  Users,
  GamepadIcon,
  Trophy,
  LifeBuoy,
  DollarSign,
  BarChart,
  Search,
  Eye,
  Calculator,
  RefreshCw,
  ChevronDown,
  Coins,
  Check,
  Settings,
  ChevronLeft,
  Menu,
  X,
  Gift,
} from "lucide-react";
import { useLocation } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { UsersTab } from "@/components/admin/UsersTab";
import { GameRoomsTab } from "@/components/admin/GameRoomsTab";
import { TournamentsTab } from "@/components/admin/TournamentsTab";
import { SupportTicketsTab } from "@/components/admin/SupportTicketsTab";
import { TransactionsTab } from "@/components/admin/TransactionsTab";
import { GameSettingsTab } from "@/components/admin/GameSettingsTab";
import AwardPlayersTab from "@/components/admin/AwardPlayersTab";
import { EnhancedHeader } from "@/components/ui/enhanced-header";
import { AppInfoTab } from "@/components/admin/AppInfo";

interface AdminStats {
  totalUsers: number;
  activeGames: number;
  activeTournaments: number;
  openSupportTickets: number;
}

interface Transaction {
  reference: string;
  userId: number;
  amount: number;
  coins: number;
  status: 'success' | 'pending' | 'failed';
  createdAt: string;
  updatedAt?: string;
  email?: string;
  message?: string;
  gatewayResponse?: any;
}

interface Game {
  id: number;
  name: string;
  creator: number;
  isStarted: boolean;
  isCompleted: boolean;
  isConsolidated: boolean;
  gameType: string;
  createdAt: string;
}

interface Tournament {
  id: number;
  name: string;
  creator: number;
  status: string;
  participants: number[];
  maxPlayers: number;
  startDate?: string;
}

interface SupportTicket {
  id: number;
  userId: number;
  subject: string;
  status: string;
  priority: string;
  createdAt: string;
}

export default function AdminPage() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Add useEffect to handle mobile sidebar
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768 && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobileMenuOpen]);

  // Define navigation items with their labels and icons
  const navItems = [
    { id: "dashboard", label: "Dashboard", icon: <BarChart className="h-5 w-5" /> },
    { id: "users", label: "Users", icon: <Users className="h-5 w-5" /> },
    { id: "games", label: "Game Rooms", icon: <GamepadIcon className="h-5 w-5" /> },
    { id: "tournaments", label: "Tournaments", icon: <Trophy className="h-5 w-5" /> },
    { id: "support", label: "Support Tickets", icon: <LifeBuoy className="h-5 w-5" /> },
    { id: "transactions", label: "Transactions", icon: <DollarSign className="h-5 w-5" /> },
    { id: "player-awards", label: "Player Awards", icon: <Gift className="h-5 w-5" /> },
    { id: "settings", label: "Game Settings", icon: <Settings className="h-5 w-5" /> },
    { id: "app-info", label: "App Info", icon: <Settings className="h-5 w-5" /> },
  ];

  // Move query before the conditional return
  const { data: stats } = useQuery<AdminStats>({
    queryKey: ["admin-stats"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/admin/stats");
      return response.json();
    },
    enabled: user?.role === "admin",
  });

  // Redirect if not admin
  if (!user || user.role !== "admin") {
    setLocation("/");
    return null;
  }

  // Render the appropriate content based on activeTab
  const renderContent = () => {
    switch (activeTab) {
      case "dashboard":
        return renderDashboard();
      case "users":
        return <UsersTab />;
      case "games":
        return <GameRoomsTab />;
      case "tournaments":
        return <TournamentsTab />;
      case "support":
        return <SupportTicketsTab />;
      case "transactions":
        return <TransactionsTab />;
      case "player-awards":
        return <AwardPlayersTab />;
      case "settings":
        return <GameSettingsTab />;
      case "app-info":
        return <AppInfoTab />;
      default:
        return renderDashboard();
    }
  };

  const renderDashboard = () => {
    const totalUsers = stats?.totalUsers ?? 0;
    const activeGames = stats?.activeGames ?? 0;
    const activeTournaments = stats?.activeTournaments ?? 0;
    const openSupportTickets = stats?.openSupportTickets ?? 0;

    return (
      <>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          {/* User Stats Card */}
          <Card className="p-4 hover:shadow-lg transition-shadow">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-full">
                <Users className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Total Users</h3>
                <p className="text-2xl font-bold">{totalUsers}</p>
              </div>
            </div>
          </Card>

          {/* Active Games Card */}
          <Card className="p-4 hover:shadow-lg transition-shadow">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-full">
                <GamepadIcon className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Active Games</h3>
                <p className="text-2xl font-bold">{activeGames}</p>
                <p className="text-sm text-muted-foreground">Live matches</p>
              </div>
            </div>
          </Card>

          {/* Tournament Stats Card */}
          <Card className="p-4 hover:shadow-lg transition-shadow">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-full">
                <Trophy className="w-8 h-8 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Active Tournaments</h3>
                <p className="text-2xl font-bold">{activeTournaments}</p>
                <p className="text-sm text-muted-foreground">In progress</p>
              </div>
            </div>
          </Card>

          {/* Support Tickets Card */}
          <Card className="p-4 hover:shadow-lg transition-shadow">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-full">
                <LifeBuoy className="w-8 h-8 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Open Tickets</h3>
                <p className="text-2xl font-bold">{openSupportTickets}</p>
                <p className="text-sm text-muted-foreground">Require attention</p>
              </div>
            </div>
          </Card>
        </div>

        {/* System Status Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart className="h-5 w-5" />
              System Status
            </CardTitle>
            <CardDescription>Overview of the platform's current state</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm font-medium">User Activity</p>
                  <div className="flex items-center gap-2">
                    <Badge variant={totalUsers > 0 ? "success" : "secondary"}>
                      {totalUsers > 0 ? "Active" : "No users"}
                    </Badge>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm font-medium">Game Servers</p>
                  <div className="flex items-center gap-2">
                    <Badge variant={activeGames > 0 ? "success" : "secondary"}>
                      {activeGames > 0 ? "Operational" : "Idle"}
                    </Badge>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm font-medium">Tournament System</p>
                  <div className="flex items-center gap-2">
                    <Badge variant={activeTournaments > 0 ? "success" : "secondary"}>
                      {activeTournaments > 0 ? "Active" : "No tournaments"}
                    </Badge>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm font-medium">Support Status</p>
                  <div className="flex items-center gap-2">
                    <Badge variant={openSupportTickets > 0 ? "destructive" : "success"}>
                      {openSupportTickets > 0 ? "Tickets need attention" : "All clear"}
                    </Badge>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </>
    );
  };

  return (
    <div>
      <EnhancedHeader
        title="Admin Dashboard"
        backPath="/games"
        showBackButton={true}
        rightContent={
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <Menu className="h-6 w-6" />
          </Button>
        }
      />

      <div className="flex min-h-screen bg-gradient-to-br from-blue-950 via-gray-900 to-black">
        {/* Desktop Sidebar Navigation */}
        <div className={cn(
          "relative transition-all duration-300 ease-in-out hidden md:flex",
          isCollapsed ? "w-16" : "w-64",
          "bg-black/60 backdrop-blur-sm border-r border-blue-500/30 p-4 flex-col"
        )}>
          <div className="flex items-center justify-between mb-6">
            {!isCollapsed && (
              <div>
                <h1 className="text-xl font-bold text-white">Admin Dashboard</h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Manage platform and user administration
                </p>
              </div>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => setIsCollapsed(!isCollapsed)}
            >
              <ChevronLeft className={cn(
                "h-4 w-4 transition-transform",
                isCollapsed && "rotate-180"
              )} />
            </Button>
          </div>
          
          <nav className="space-y-1 flex-1">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors",
                  activeTab === item.id
                    ? "bg-blue-600/20 text-blue-400 font-medium"
                    : "text-gray-300 hover:bg-blue-600/10 hover:text-blue-300"
                )}
              >
                {item.icon}
                {!isCollapsed && item.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Mobile Sidebar Navigation */}
        <div className={cn(
          "fixed inset-0 z-50 bg-black/60 backdrop-blur-sm md:hidden",
          isMobileMenuOpen ? "opacity-100 pointer-events-auto mt-10" : "opacity-0 pointer-events-none"
        )}>
          <div className={cn(
            "absolute inset-y-0 left-0 w-64 bg-black/90 border-r border-blue-500/30 p-4 transition-transform duration-300",
            isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
          )}>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-xl font-bold text-white">Admin Dashboard</h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Manage platform and user administration
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
            
            <nav className="space-y-1">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => {
                    setActiveTab(item.id);
                    setIsMobileMenuOpen(false);
                  }}
                  className={cn(
                    "w-full flex items-center gap-3 px-4 py-3 rounded-md text-base transition-colors",
                    activeTab === item.id
                      ? "bg-blue-600/20 text-blue-400 font-medium"
                      : "text-gray-300 hover:bg-blue-600/10 hover:text-blue-300"
                  )}
                >
                  {item.icon}
                  {item.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Accessible backdrop button */}
          <button
            className="absolute inset-0 -z-10 w-full h-full cursor-default"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-label="Close menu"
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setIsMobileMenuOpen(false);
              }
            }}
          />
        </div>

        {/* Main Content Area */}
        <div className="flex-1 p-6 overflow-auto pb-20">
          <div className="max-w-6xl mx-auto space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold">{navItems.find(item => item.id === activeTab)?.label}</h2>
            </div>
            
            {/* Content based on selected tab */}
            <div className="space-y-4">
              {renderContent()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function GameManagement() {
  const [, setLocation] = useLocation();
  const {
    data: games,
    isLoading,
    refetch,
  } = useQuery<Game[]>({
    queryKey: ["admin-games"],
    queryFn: async () => {
      const response = await apiRequest("/api/admin/games", "GET");
      return response.json();
    },
  });

  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  // Mutation for consolidating game points
  const consolidatePointsMutation = useMutation({
    mutationFn: async (gameId: number) => {
      const response = await apiRequest(`/api/admin/games/${gameId}/consolidate-points`, "POST");
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Points consolidated",
        description: "Game points have been successfully consolidated",
      });
      refetch();
    },
    onError: (error: any) => {
      toast({
        title: "Error consolidating points",
        description:
          error.message ?? "An error occurred while consolidating points",
        variant: "destructive",
      });
    },
  });

  const handleViewGameDetails = (gameId: number) => {
    setLocation(`/admin/game-room/${gameId}`);
  };

  const handleConsolidatePoints = (gameId: number) => {
    consolidatePointsMutation.mutate(gameId);
  };

  const getGameStatus = (game: any) => {
    if (game.isCompleted) return "completed";
    if (game.isStarted) return "in_progress";
    return "pending";
  };

  const getGameStatusStyle = (game: any) => {
    if (game.isCompleted) return "bg-emerald-600 hover:bg-emerald-700 text-white";
    if (game.isStarted) return "bg-blue-600 hover:bg-blue-700 text-white";
    return "bg-gray-600 hover:bg-gray-700 text-white";
  };

  const getGameStatusText = (game: any) => {
    if (game.isCompleted) return "Completed";
    if (game.isStarted) return "In Progress";
    return "Pending";
  };

  // Filter games based on search query and filters
  const filteredGames = games
    ? games.filter((game: Game) => {
        // Search filter
        const matchesSearch = [
          game.name?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false,
          game.creator?.toString().includes(searchQuery) ?? false,
          game.id?.toString().includes(searchQuery) ?? false
        ].some(Boolean);

        // Status filter
        const status = getGameStatus(game);
        const matchesStatus = statusFilter === "all" || statusFilter === status;

        // Type filter
        const matchesType =
          typeFilter === "all" ||
          (game.gameType && game.gameType === typeFilter);

        return matchesSearch && matchesStatus && matchesType;
      })
    : [];

  // Get unique game types for the filter dropdown
  const gameTypes = games
    ? Array.from(new Set(games.map((game: any) => game.gameType))).filter(
        Boolean,
      )
    : [];

  if (isLoading) {
    return <div className="text-center py-8">Loading game rooms...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between mb-4">
        <h2 className="text-2xl font-semibold">Game Room Management</h2>
      </div>

      {/* Search and filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by name, ID or creator..."
              className="pl-9 w-full rounded-md border border-input bg-slate-800 py-2 text-sm text-white ring-offset-background placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="flex gap-2">
          <select
            className="rounded-md border border-input bg-slate-800 px-3 py-2 text-sm text-white ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="completed">Completed</option>
            <option value="in_progress">In Progress</option>
            <option value="pending">Pending</option>
          </select>

          <select
            className="rounded-md border border-input bg-slate-800 px-3 py-2 text-sm text-white ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="all">All Types</option>
            {gameTypes.map((type) => (
              <option key={String(type)} value={String(type)}>
                {String(type)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {!games || games.length === 0 ? (
        <div className="text-center py-8 border rounded-lg bg-muted/50">
          No game rooms found
        </div>
      ) : filteredGames.length === 0 ? (
        <div className="text-center py-8 border rounded-lg bg-muted/50">
          No game rooms match your search criteria
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-2">ID</th>
                <th className="text-left p-2">Name</th>
                <th className="text-left p-2">Creator</th>
                <th className="text-left p-2">Status</th>
                <th className="text-left p-2">Type</th>
                <th className="text-left p-2">Created</th>
                <th className="text-left p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredGames.map((game: any) => (
                <tr key={game.id} className="border-t hover:bg-muted/50">
                  <td className="p-2">{game.id}</td>
                  <td className="p-2">{game.name}</td>
                  <td className="p-2">{game.creator}</td>
                  <td className="p-2">
                    <Badge className={getGameStatusStyle(game)}>
                      {getGameStatusText(game)}
                    </Badge>
                  </td>
                  <td className="p-2">
                    <Badge className="bg-muted hover:bg-muted/80 text-foreground">
                      {game.gameType}
                    </Badge>
                  </td>
                  <td className="p-2">
                    {new Date(game.createdAt).toLocaleDateString()}
                  </td>
                  <td className="p-2">
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewGameDetails(game.id)}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>

                      <Button
                        variant="outline"
                        size="sm"
                        className={
                          !game.isCompleted || game.isConsolidated
                            ? "opacity-50 cursor-not-allowed"
                            : ""
                        }
                        disabled={!game.isCompleted || game.isConsolidated}
                        onClick={() => handleConsolidatePoints(game.id)}
                      >
                        <Calculator className="h-4 w-4 mr-1" />
                        Consolidate
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TournamentManagement() {
  const [, setLocation] = useLocation();
  const {
    data: tournaments,
    isLoading,
    refetch,
  } = useQuery<Tournament[]>({
    queryKey: ["admin-tournaments"],
    queryFn: async () => {
      const response = await apiRequest("/api/admin/tournaments", "GET");
      return response.json();
    },
  });

  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Mutation for updating tournament status
  const updateTournamentMutation = useMutation({
    mutationFn: async ({
      tournamentId,
      status,
    }: {
      tournamentId: number;
      status: string;
    }) => {
      const response = await apiRequest(`/api/admin/tournaments/${tournamentId}`, "PATCH", { status });
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Tournament updated",
        description: "Tournament status has been successfully updated",
      });
      refetch();
    },
    onError: (error: any) => {
      toast({
        title: "Error updating tournament",
        description: error.message ?? "An error occurred while updating the tournament",
        variant: "destructive",
      });
    },
  });

  const handleViewTournament = (tournamentId: number) => {
    setLocation(`/admin/tournament/${tournamentId}`);
  };

  const handleUpdateStatus = (tournamentId: number, status: string) => {
    updateTournamentMutation.mutate({ tournamentId, status });
  };

  // Filter tournaments based on search query and status filter
  const filteredTournaments = tournaments
    ? tournaments.filter((tournament: Tournament) => {
        // Search filter
        const matchesSearch = [
          tournament.name?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false,
          tournament.creator?.toString().includes(searchQuery) ?? false,
          tournament.id?.toString().includes(searchQuery) ?? false
        ].some(Boolean);

        // Status filter
        const matchesStatus =
          statusFilter === "all" || statusFilter === tournament.status;

        return matchesSearch && matchesStatus;
      })
    : [];

  if (isLoading) {
    return <div className="text-center py-8">Loading tournaments...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between mb-4">
        <h2 className="text-2xl font-semibold">Tournament Management</h2>
      </div>

      {/* Search and filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by name, ID or creator..."
              className="pl-9 w-full rounded-md border border-input bg-slate-800 py-2 text-sm text-white ring-offset-background placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="flex gap-2">
          <select
            className="rounded-md border border-input bg-slate-800 px-3 py-2 text-sm text-white ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>

          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setSearchQuery("");
              setStatusFilter("all");
            }}
          >
            <RefreshCw className="h-4 w-4 mr-1" />
            Reset Filters
          </Button>
        </div>
      </div>

      {!tournaments || tournaments.length === 0 ? (
        <div className="text-center py-8 border rounded-lg bg-muted/50">
          No tournaments found
        </div>
      ) : filteredTournaments.length === 0 ? (
        <div className="text-center py-8 border rounded-lg bg-muted/50">
          No tournaments match your search criteria
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-2">ID</th>
                <th className="text-left p-2">Name</th>
                <th className="text-left p-2">Creator</th>
                <th className="text-left p-2">Status</th>
                <th className="text-left p-2">Players</th>
                <th className="text-left p-2">Start Date</th>
                <th className="text-left p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTournaments.map((tournament: any) => (
                <tr key={tournament.id} className="border-t hover:bg-muted/50">
                  <td className="p-2">{tournament.id}</td>
                  <td className="p-2">{tournament.name}</td>
                  <td className="p-2">{tournament.creator}</td>
                  <td className="p-2">
                    <Badge
                      className={
                        tournament.status === "completed"
                          ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                          : tournament.status === "active"
                            ? "bg-blue-600 hover:bg-blue-700 text-white"
                            : tournament.status === "cancelled"
                              ? "bg-red-600 hover:bg-red-700 text-white"
                              : "bg-gray-600 hover:bg-gray-700 text-white"
                      }
                    >
                      {tournament.status}
                    </Badge>
                  </td>
                  <td className="p-2">
                    {Array.isArray(tournament.participants)
                      ? tournament.participants.length
                      : 0}
                    /{tournament.maxPlayers}
                  </td>
                  <td className="p-2">
                    {tournament.startDate
                      ? new Date(tournament.startDate).toLocaleDateString()
                      : "Not Started"}
                  </td>
                  <td className="p-2">
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewTournament(tournament.id)}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>

                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm">
                            Status <ChevronDown className="h-4 w-4 ml-1" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Change Status</DropdownMenuLabel>
                          <DropdownMenuItem
                            onClick={() =>
                              handleUpdateStatus(tournament.id, "pending")
                            }
                            disabled={tournament.status === "pending"}
                          >
                            Set to Pending
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() =>
                              handleUpdateStatus(tournament.id, "active")
                            }
                            disabled={
                              tournament.status === "active" ||
                              tournament.status === "completed"
                            }
                          >
                            Set to Active
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() =>
                              handleUpdateStatus(tournament.id, "completed")
                            }
                            disabled={tournament.status === "completed"}
                          >
                            Set to Completed
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() =>
                              handleUpdateStatus(tournament.id, "cancelled")
                            }
                            disabled={tournament.status === "cancelled"}
                          >
                            Set to Cancelled
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TransactionsManagement() {
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateRangeFilter, setDateRangeFilter] = useState("all");
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [selectedVerifyRef, setSelectedVerifyRef] = useState<string>("");

  // Query to get all transactions
  const {
    data: transactions,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["admin-transactions"],
    queryFn: async () => {
      const response = await apiRequest("/api/currency/admin-transactions", "GET");
      const data = await response.json();
      return data.data as Transaction[];
    },
  });

  // Function to load transaction details
  const fetchTransactionDetails = async (reference: string) => {
    try {
      const response = await apiRequest(`/api/currency/admin-transactions/${reference}`, "GET");
      const data = await response.json();
      if (data.success) {
        setSelectedTransaction(data.data ?? {});
        setIsDetailModalOpen(true);
      } else {
        throw new Error(data.message ?? 'Failed to fetch transaction details');
      }
    } catch (error) {
      toast({
        title: "Error fetching transaction details",
        description: error instanceof Error ? error.message : "Failed to load transaction details",
        variant: "destructive",
      });
    }
  };

  // Add verification mutation
  const verifyTransactionMutation = useMutation({
    mutationFn: async (reference: string) => {
      const response = await apiRequest(`/api/currency/verify-transaction/${reference}`, "POST");
      return response.json();
    },
    onSuccess: (data: any) => {
      toast({
        title: data.success ? "Transaction verified" : "Verification failed",
        description: data.message ?? (data.success ? "Payment verified and coins added" : "Could not verify payment"),
        variant: data.success ? "default" : "destructive"
      });
      refetch();
      if (data.success) {
        setIsDetailModalOpen(false);
      }
    },
    onError: (error: any) => {
      toast({
        title: "Verification failed",
        description: error.message ?? "Could not verify the transaction",
        variant: "destructive"
      });
    }
  });

  const handleVerifyTransaction = (reference: string) => {
    verifyTransactionMutation.mutate(reference);
  };

  const handleVerifyClick = (reference: string) => {
    setSelectedVerifyRef(reference);
    setVerifyDialogOpen(true);
  };

  const handleVerifyConfirm = () => {
    if (selectedVerifyRef) {
      handleVerifyTransaction(selectedVerifyRef);
      setVerifyDialogOpen(false);
    }
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  // Calculate date ranges for filter
  const getDateRangeFilter = (transaction: any) => {
    const date = new Date(transaction.createdAt);
    const now = new Date();
    const daysDiff = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

    if (daysDiff < 1) return "today";
    if (daysDiff < 7) return "week";
    if (daysDiff < 30) return "month";
    return "older";
  };

  // Filter transactions based on search query and filters
  const filteredTransactions = Array.isArray(transactions)
    ? transactions.filter((transaction: any) => {
        // Search filter
        const matchesSearch = [
          transaction.reference?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false,
          transaction.userId?.toString().includes(searchQuery) ?? false,
          transaction.email?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false
        ].some(Boolean);

        // Status filter
        const matchesStatus = statusFilter === "all" || transaction.status === statusFilter;

        // Date range filter
        const matchesDateRange =
          dateRangeFilter === "all" || getDateRangeFilter(transaction) === dateRangeFilter;

        return matchesSearch && matchesStatus && matchesDateRange;
      })
    : [];

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <RefreshCw className="animate-spin h-8 w-8" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-destructive/10 rounded-lg text-center">
        <h3 className="text-lg font-medium">Error loading transactions</h3>
        <p className="text-muted-foreground">{error instanceof Error ? error.message : 'An unknown error occurred'}</p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => refetch()}
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between mb-4">
        <h2 className="text-2xl font-semibold">Transaction Management</h2>
      </div>

      {/* Search and filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by reference or user ID..."
              className="pl-9 w-full rounded-md border border-input bg-slate-800 py-2 text-sm text-white ring-offset-background placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="flex gap-2">
          <select
            className="rounded-md border border-input bg-slate-800 px-3 py-2 text-sm text-white ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="success">Success</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
          </select>

          <select
            className="rounded-md border border-input bg-slate-800 px-3 py-2 text-sm text-white ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            value={dateRangeFilter}
            onChange={(e) => setDateRangeFilter(e.target.value)}
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="older">Older</option>
          </select>
        </div>
      </div>

      {!transactions || !Array.isArray(transactions) || transactions.length === 0 ? (
        <div className="text-center py-8 border rounded-lg bg-muted/50">
          No transactions found
        </div>
      ) : filteredTransactions.length === 0 ? (
        <div className="text-center py-8 border rounded-lg bg-muted/50">
          No transactions match your search criteria
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-2">Reference</th>
                <th className="text-left p-2">User ID</th>
                <th className="text-left p-2">Amount</th>
                <th className="text-left p-2">Coins</th>
                <th className="text-left p-2">Date</th>
                <th className="text-left p-2">Status</th>
                <th className="text-left p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTransactions.map((transaction: any) => (
                <tr
                  key={transaction.reference}
                  className="border-t hover:bg-muted/50"
                >
                  <td className="p-2 font-mono text-xs">{transaction.reference}</td>
                  <td className="p-2">{transaction.userId}</td>
                  <td className="p-2">GHS {transaction.amount}</td>
                  <td className="p-2">
                    <span className="flex items-center">
                      <Coins className="h-4 w-4 mr-1 text-yellow-500" />
                      {transaction.coins}
                    </span>
                  </td>
                  <td className="p-2 text-sm">{formatDate(transaction.createdAt)}</td>
                  <td className="p-2">
                    <Badge
                      className={
                        transaction.status === "success" || transaction.status === "completed"
                          ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                          : transaction.status === "pending"
                            ? "bg-amber-600 hover:bg-amber-700 text-white"
                            : "bg-rose-600 hover:bg-rose-700 text-white"
                      }
                    >
                      {transaction.status === "success" ? "completed" : transaction.status}
                    </Badge>
                  </td>
                  <td className="p-2">
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fetchTransactionDetails(transaction.reference)}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Details
                      </Button>
                      
                      {transaction.status === "pending" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleVerifyClick(transaction.reference)}
                          disabled={verifyTransactionMutation.isPending}
                        >
                          {verifyTransactionMutation.isPending ? (
                            <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                          ) : (
                            <Check className="h-4 w-4 mr-1" />
                          )}
                          Verify
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Verification Dialog */}
      <AlertDialog open={verifyDialogOpen} onOpenChange={setVerifyDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Verify Payment</AlertDialogTitle>
            <AlertDialogDescription>
              This will check the payment status with the payment gateway. If successful, coins will be added to the user's account. Do you want to proceed?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button
              onClick={handleVerifyConfirm}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              disabled={verifyTransactionMutation.isPending}
            >
              {verifyTransactionMutation.isPending ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Verifying...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Verify Payment
                </>
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Transaction Details Dialog */}
      <Dialog open={isDetailModalOpen} onOpenChange={setIsDetailModalOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Transaction Details</DialogTitle>
            <DialogDescription>
              Detailed information about this transaction
            </DialogDescription>
          </DialogHeader>
          
          {selectedTransaction && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">Reference:</p>
                  <p className="font-mono text-sm">{selectedTransaction.reference}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">Status:</p>
                  <Badge
                    className={
                      selectedTransaction.status === "success" || selectedTransaction.status === "completed"
                        ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                        : selectedTransaction.status === "pending"
                          ? "bg-amber-600 hover:bg-amber-700 text-white"
                          : "bg-rose-600 hover:bg-rose-700 text-white"
                    }
                  >
                    {selectedTransaction.status === "success" ? "completed" : selectedTransaction.status}
                  </Badge>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">User ID:</p>
                  <p>{selectedTransaction.userId}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">Email:</p>
                  <p>{selectedTransaction.email ?? "Not provided"}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">Amount:</p>
                  <p>GHS {selectedTransaction.amount}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">Coins:</p>
                  <p className="flex items-center">
                    <Coins className="h-4 w-4 mr-1 text-yellow-500" />
                    {selectedTransaction.coins}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">Created At:</p>
                  <p>{formatDate(selectedTransaction.createdAt)}</p>
                </div>
                {selectedTransaction.updatedAt && (
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-muted-foreground">Updated At:</p>
                    <p>{formatDate(selectedTransaction.updatedAt)}</p>
                  </div>
                )}
              </div>

              {selectedTransaction.message && (
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">Message:</p>
                  <p className="text-sm">{selectedTransaction.message}</p>
                </div>
              )}

              {selectedTransaction.gatewayResponse && (
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">Gateway Response:</p>
                  <pre className="text-xs bg-muted p-2 rounded overflow-x-auto">
                    {typeof selectedTransaction.gatewayResponse === 'object'
                      ? JSON.stringify(selectedTransaction.gatewayResponse, null, 2)
                      : selectedTransaction.gatewayResponse}
                  </pre>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            {selectedTransaction?.status === "pending" && (
              <Button
                variant="secondary"
                onClick={() => handleVerifyClick(selectedTransaction.reference)}
                disabled={verifyTransactionMutation.isPending}
              >
                {verifyTransactionMutation.isPending ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Verify Payment
                  </>
                )}
              </Button>
            )}
            <Button onClick={() => setIsDetailModalOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SupportTicketManagement() {
  const [, setLocation] = useLocation();
  const { data: tickets, isLoading } = useQuery<SupportTicket[]>({
    queryKey: ["admin-support-tickets"],
    queryFn: async () => {
      const response = await apiRequest("/api/admin/support-tickets", "GET");
      return response.json();
    },
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");

  const handleManageTicket = (ticketId: number) => {
    setLocation(`/admin/support/${ticketId}`);
  };

  // Filter tickets based on search query and filters
  const filteredTickets = tickets
    ? tickets.filter((ticket: SupportTicket) => {
        // Search filter
        const matchesSearch = [
          ticket.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false,
          ticket.id?.toString().includes(searchQuery) ?? false,
          ticket.userId?.toString().includes(searchQuery) ?? false
        ].some(Boolean);

        // Status filter
        const matchesStatus =
          statusFilter === "all" || ticket.status === statusFilter;

        // Priority filter
        const matchesPriority =
          priorityFilter === "all" || ticket.priority === priorityFilter;

        return matchesSearch && matchesStatus && matchesPriority;
      })
    : [];

  if (isLoading) {
    return <div className="text-center py-8">Loading support tickets...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold mb-4">Support Tickets</h2>
        <Button
          onClick={() => setLocation("/admin/support")}
          className="flex items-center gap-2"
        >
          <LifeBuoy className="w-4 h-4" />
          Manage All Tickets
        </Button>
      </div>

      {/* Search and filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by subject, ID or user ID..."
              className="pl-9 w-full rounded-md border border-input bg-slate-800 py-2 text-sm text-white ring-offset-background placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="flex gap-2">
          <select
            className="rounded-md border border-input bg-slate-800 px-3 py-2 text-sm text-white ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="closed">Closed</option>
          </select>

          <select
            className="rounded-md border border-input bg-slate-800 px-3 py-2 text-sm text-white ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
          >
            <option value="all">All Priorities</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>

          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setSearchQuery("");
              setStatusFilter("all");
              setPriorityFilter("all");
            }}
          >
            <RefreshCw className="h-4 w-4 mr-1" />
            Reset
          </Button>
        </div>
      </div>

      {!tickets || tickets.length === 0 ? (
        <div className="text-center py-8 border rounded-lg bg-muted/50">
          No support tickets found
        </div>
      ) : filteredTickets.length === 0 ? (
        <div className="text-center py-8 border rounded-lg bg-muted/50">
          No tickets match your search criteria
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-2">ID</th>
                <th className="text-left p-2">User</th>
                <th className="text-left p-2">Subject</th>
                <th className="text-left p-2">Status</th>
                <th className="text-left p-2">Priority</th>
                <th className="text-left p-2">Created</th>
                <th className="text-left p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTickets.map((ticket: any) => (
                <tr key={ticket.id} className="border-t hover:bg-muted/50">
                  <td className="p-2">{ticket.id}</td>
                  <td className="p-2">{ticket.userId}</td>
                  <td className="p-2 max-w-[200px] truncate">
                    {ticket.subject}
                  </td>
                  <td className="p-2">
                    <Badge
                      className={
                        ticket.status === "open"
                          ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                          : ticket.status === "in_progress"
                            ? "bg-blue-600 hover:bg-blue-700 text-white"
                            : "bg-green-600 hover:bg-green-700 text-white"
                      }
                    >
                      {ticket.status.replace("_", " ")}
                    </Badge>
                  </td>
                  <td className="p-2">
                    <Badge
                      className={
                        ticket.priority === "high"
                          ? "bg-red-600 hover:bg-red-700 text-white"
                          : ticket.priority === "medium"
                            ? "bg-yellow-600 hover:bg-yellow-700 text-white"
                            : "bg-green-600 hover:bg-green-700 text-white"
                      }
                    >
                      {ticket.priority}
                    </Badge>
                  </td>
                  <td className="p-2">
                    {new Date(ticket.createdAt).toLocaleDateString()}
                  </td>
                  <td className="p-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleManageTicket(ticket.id)}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Manage
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
